import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ShipmentDetails from '../components/ShipmentDetails';
import './DeliveryNoteDetails.css';
import './DeliveryNoteCreation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getPalletInfo = (item, palletNumber) =>
  item.inventory?.pallets?.find((p) => p.pallet_number === palletNumber);

const isPalletSelectable = (palletInfo, deliveryId) => {
  if (!palletInfo) return true;
  if (palletInfo.status === 'available') return true;
  return palletInfo.delivery_id === deliveryId;
};

const buildSelectedPalletsFromItems = (items) => {
  const selected = {};
  (items || []).forEach((item) => {
    const cargoKey = `cargo-${item.cargo_id}`;
    if (!selected[cargoKey]) {
      selected[cargoKey] = [];
    }
    if (item.pallet_number && !selected[cargoKey].includes(item.pallet_number)) {
      selected[cargoKey].push(item.pallet_number);
    }
  });
  return selected;
};

const buildSelectedItemNumbersFromItems = (items) => {
  const numbers = new Set();
  (items || []).forEach((item) => {
    if (item.pallet_number) {
      numbers.add(String(item.pallet_number));
    }
  });
  return Array.from(numbers);
};

function parseApiDateTime(isoString) {
  if (!isoString) return null;
  const hasOffset = isoString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoString);
  const normalized = hasOffset ? isoString : `${isoString}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDatetimeLocalValue(isoString) {
  const date = parseApiDateTime(isoString);
  if (!date) return '';
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

async function readApiError(response, fallbackMessage) {
  const data = await response.json().catch(() => ({}));
  return data.error || fallbackMessage;
}

const buildPalletWeightsFromItems = (items) => {
  const weights = {};
  (items || []).forEach((item) => {
    if (!item.pallet_number) {
      return;
    }
    const key = String(item.pallet_number);
    if (weights[key] === undefined) {
      weights[key] = item.pallet_weight_measured_kg ?? '';
    }
  });
  return weights;
};

export default function DeliveryNoteDetails({ token, user }) {
  const { deliveryId } = useParams();
  const navigate = useNavigate();

  const [deliveryNote, setDeliveryNote] = useState(null);
  const [shipment, setShipment] = useState(null);
  const [packing_list, setPackingList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showDepartureModal, setShowDepartureModal] = useState(false);
  const [departureDateTime, setDepartureDateTime] = useState('');
  const [showArrivalModal, setShowArrivalModal] = useState(false);
  const [arrivalDateTime, setArrivalDateTime] = useState('');
  const [documentViewer, setDocumentViewer] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [selectedPallets, setSelectedPallets] = useState({});
  const [selectedItemNumbers, setSelectedItemNumbers] = useState([]);
  const [palletFilter, setPalletFilter] = useState('');
  const [palletWeights, setPalletWeights] = useState({});
  const [savingWeights, setSavingWeights] = useState(false);
  const [showWeightMismatchModal, setShowWeightMismatchModal] = useState(false);
  const [weightMismatchLoadDraft, setWeightMismatchLoadDraft] = useState('');
  const [savingLoadWeight, setSavingLoadWeight] = useState(false);
  const [pendingWeightAction, setPendingWeightAction] = useState(null);
  const [manualAllocSelections, setManualAllocSelections] = useState([]);
  const [manualAllocReason, setManualAllocReason] = useState('');
  const [manualAllocating, setManualAllocating] = useState(false);
  const [allocationLogs, setAllocationLogs] = useState([]);

  const isAdmin = user?.role === 'admin';
  const isMixed = packing_list?.pallet_mode === 'mixed';
  const mixedPallets = packing_list?.mixed_pallets_delivery || [];
  const cargoItems = packing_list?.cargo_items || [];
  const canEditPallets = deliveryNote?.status === 'draft';
  const canEditWeights = ['draft', 'ready'].includes(deliveryNote?.status);
  const canManualAllocate = isAdmin && deliveryNote && packing_list;

  const getFilterDigits = () => palletFilter.replace(/\D/g, '');
  const isPalletFilterActive = () => getFilterDigits().length >= 3;

  const getMixedPalletsToShow = () => {
    const digits = getFilterDigits();
    if (digits.length < 3) {
      return mixedPallets;
    }
    return mixedPallets.filter((pallet) => String(pallet.pallet_number).includes(digits));
  };

  const getFilteredPallets = () => {
    const digits = getFilterDigits();
    if (digits.length < 3) {
      return [];
    }

    const results = [];
    cargoItems.forEach((item) => {
      (item.pallet_numbers || []).forEach((palletNumber) => {
        if (String(palletNumber).includes(digits)) {
          results.push({ item, palletNumber });
        }
      });
    });
    return results;
  };

  const getUniqueDeliveryPallets = () => {
    const items = deliveryNote?.items || [];
    if (isMixed) {
      const byNumber = {};
      items.forEach((item) => {
        const key = String(item.pallet_number);
        if (!byNumber[key]) {
          byNumber[key] = {
            pallet_number: key,
            materials: [],
            pallet_weight_measured_kg: palletWeights[key] ?? item.pallet_weight_measured_kg ?? '',
          };
        }
        byNumber[key].materials.push({
          material_description: item.material_description,
          quantity_delivered: item.quantity_delivered,
        });
      });
      return Object.values(byNumber);
    }
    return items.map((item) => {
      const key = String(item.pallet_number);
      return {
        pallet_number: key,
        material_description: item.material_description,
        quantity_delivered: item.quantity_delivered,
        pallet_weight_measured_kg: palletWeights[key] ?? item.pallet_weight_measured_kg ?? '',
      };
    });
  };

  const getPalletWeightValue = (row) => {
    const raw = palletWeights[String(row.pallet_number)] ?? row.pallet_weight_measured_kg;
    if (raw === '' || raw === null || raw === undefined) {
      return null;
    }
    const num = parseFloat(raw);
    return Number.isFinite(num) ? num : null;
  };

  const getPalletWeightTotal = () =>
    getUniqueDeliveryPallets().reduce((sum, row) => {
      const value = getPalletWeightValue(row);
      return value == null ? sum : sum + value;
    }, 0);

  const allPalletWeightsEntered = () => {
    const rows = getUniqueDeliveryPallets();
    return rows.length > 0 && rows.every((row) => getPalletWeightValue(row) != null);
  };

  const getTotalLoadWeightValue = () => {
    const raw = isEditing ? editForm.total_load_weight_kg : deliveryNote?.total_load_weight_kg;
    if (raw === '' || raw === null || raw === undefined) {
      return null;
    }
    const num = parseFloat(raw);
    return Number.isFinite(num) ? num : null;
  };

  const WEIGHT_MATCH_TOLERANCE = 0.009;

  const weightsMatchTotalLoad = () => {
    const loadWeight = getTotalLoadWeightValue();
    if (loadWeight == null || !allPalletWeightsEntered()) {
      return true;
    }
    return Math.abs(getPalletWeightTotal() - loadWeight) <= WEIGHT_MATCH_TOLERANCE;
  };

  const openWeightMismatchModal = (pendingAction = null) => {
    setPendingWeightAction(pendingAction);
    setWeightMismatchLoadDraft(getPalletWeightTotal().toFixed(2));
    setShowWeightMismatchModal(true);
  };

  const ensureWeightTotalMatches = (pendingAction = null) => {
    if (!allPalletWeightsEntered() || getTotalLoadWeightValue() == null) {
      return true;
    }
    if (weightsMatchTotalLoad()) {
      return true;
    }
    openWeightMismatchModal(pendingAction);
    return false;
  };

  useEffect(() => {
    return () => {
      if (documentViewer?.url) {
        window.URL.revokeObjectURL(documentViewer.url);
      }
    };
  }, [documentViewer]);

  useEffect(() => {
    if (token) {
      fetchDeliveryNote();
    }
  }, [deliveryId, token]);

  const closeDocumentViewer = () => {
    setDocumentViewer((current) => {
      if (current?.url) {
        window.URL.revokeObjectURL(current.url);
      }
      return null;
    });
  };

  const handleDocument = async (docType, action) => {
    try {
      setError('');
      setDocumentLoading(true);

      const endpoint = docType === 'signed'
        ? `${API_URL}/delivery-notes/${deliveryId}/download-signed`
        : `${API_URL}/delivery-notes/${deliveryId}/pdf`;
      const disposition = action === 'view' ? 'view' : 'download';

      const response = await fetch(`${endpoint}?disposition=${disposition}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to load document');
      }

      const blob = await response.blob();
      const contentType = response.headers.get('content-type') || blob.type;

      if (action === 'view') {
        closeDocumentViewer();
        const url = window.URL.createObjectURL(blob);
        const title = docType === 'signed'
          ? `Signed Copy — ${deliveryNote.delivery_number}`
          : `Delivery Note — ${deliveryNote.delivery_number}`;
        const type = contentType.includes('pdf') ? 'pdf' : 'image';
        setDocumentViewer({ url, title, type });
        return;
      }

      const ext = contentType.includes('pdf') ? 'pdf' : 'jpg';
      const filename = docType === 'signed'
        ? `signed-${deliveryNote.delivery_number}.${ext}`
        : `delivery-note-${deliveryNote.delivery_number}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setDocumentLoading(false);
    }
  };

  const fetchDeliveryNote = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Delivery note not found');
      }

      const data = await response.json();
      setDeliveryNote(data);
      setEditForm({
        delivery_date: data.delivery_date || '',
        customer_name: data.customer_name || '',
        delivery_address: data.delivery_address || '',
        delivery_city: data.delivery_city || '',
        delivery_country: data.delivery_country || '',
        truck_model: data.truck_model || '',
        truck_registration: data.truck_registration || '',
        driver_name: data.driver_name || '',
        driver_phone: data.driver_phone || '',
        notes: data.notes || '',
        total_load_weight_kg: data.total_load_weight_kg ?? '',
        total_pallet_count: data.total_pallet_count ?? ''
      });
      setSelectedPallets(buildSelectedPalletsFromItems(data.items));
      setSelectedItemNumbers(buildSelectedItemNumbersFromItems(data.items));
      setPalletWeights(buildPalletWeightsFromItems(data.items));
      setPalletFilter('');
      setManualAllocSelections([]);
      setManualAllocReason('');

      const packingListId = data.packing_list_id;
      if (data.shipment_id) {
        const shipmentRes = await fetch(`${API_URL}/shipments/${data.shipment_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (shipmentRes.ok) {
          const shipmentData = await shipmentRes.json();
          setShipment(shipmentData);
        }
      }

      const plId = packingListId;
      if (plId) {
        const packingRes = await fetch(`${API_URL}/packing-lists/${plId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (packingRes.ok) {
          const packingData = await packingRes.json();
          setPackingList(packingData);
        }
      }

      if (user?.role === 'admin') {
        const logsRes = await fetch(`${API_URL}/delivery-notes/${deliveryId}/logs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (logsRes.ok) {
          const logsData = await logsRes.json();
          setAllocationLogs(logsData);
        }
      }

      setLoading(false);
    } catch (err) {
      setLoadError(err.message);
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setSelectedPallets(buildSelectedPalletsFromItems(deliveryNote.items));
    setSelectedItemNumbers(buildSelectedItemNumbersFromItems(deliveryNote.items));
    setPalletFilter('');
    setIsEditing(true);
  };

  const buildItemWeightsPayload = () => ({
    item_weights: Object.entries(palletWeights).map(([pallet_number, weight]) => ({
      pallet_number,
      pallet_weight_measured_kg: weight === '' || weight === null || weight === undefined
        ? null
        : weight,
    })),
  });

  const buildPalletPayload = () => {
    if (!canEditPallets) {
      return {};
    }

    if (isMixed) {
      return { pallet_numbers: selectedItemNumbers };
    }

    const items = [];
    let sequenceNumber = 1;
    Object.entries(selectedPallets).forEach(([cargoKey, pallets]) => {
      const cargoId = parseInt(cargoKey.split('-')[1], 10);
      const cargo = cargoItems.find((c) => c.cargo_id === cargoId);

      if (cargo && pallets.length > 0) {
        pallets.forEach((palletNumber) => {
          const palletInfo = getPalletInfo(cargo, palletNumber);
          const weight = palletWeights[String(palletNumber)];
          items.push({
            cargo_id: cargoId,
            pallet_number: palletNumber,
            quantity_delivered: cargo.pallet_count ?? palletInfo?.quantity ?? cargo.quantity_pieces,
            sequence_number: sequenceNumber++,
            pallet_weight_measured_kg: weight === '' || weight === null || weight === undefined
              ? null
              : weight,
          });
        });
      }
    });
    return { items };
  };

  const handleSave = async () => {
    try {
      const payload = { ...editForm, ...buildPalletPayload(), ...buildItemWeightsPayload() };

      if (canEditPallets) {
        if (isMixed && selectedItemNumbers.length === 0) {
          setError('Please select at least one item number on this delivery note');
          return;
        }
        if (!isMixed) {
          const itemCount = Object.values(selectedPallets).reduce(
            (sum, pallets) => sum + pallets.length,
            0
          );
          if (itemCount === 0) {
            setError('Please select at least one pallet on this delivery note');
            return;
          }
        }
      }

      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update delivery note');
      }

      setSuccess('Delivery note updated successfully');
      setIsEditing(false);
      setTimeout(() => fetchDeliveryNote(), 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      delivery_date: deliveryNote.delivery_date || '',
      customer_name: deliveryNote.customer_name || '',
      delivery_address: deliveryNote.delivery_address || '',
      delivery_city: deliveryNote.delivery_city || '',
      delivery_country: deliveryNote.delivery_country || '',
      truck_model: deliveryNote.truck_model || '',
      truck_registration: deliveryNote.truck_registration || '',
      driver_name: deliveryNote.driver_name || '',
      driver_phone: deliveryNote.driver_phone || '',
      notes: deliveryNote.notes || '',
      total_load_weight_kg: deliveryNote.total_load_weight_kg ?? '',
      total_pallet_count: deliveryNote.total_pallet_count ?? ''
    });
    setSelectedPallets(buildSelectedPalletsFromItems(deliveryNote.items));
    setSelectedItemNumbers(buildSelectedItemNumbersFromItems(deliveryNote.items));
    setPalletWeights(buildPalletWeightsFromItems(deliveryNote.items));
    setPalletFilter('');
  };

  const handlePalletToggle = (cargoId, palletNumber, item) => {
    const palletInfo = getPalletInfo(item, palletNumber);
    if (!isPalletSelectable(palletInfo, deliveryNote.delivery_id)) {
      return;
    }

    setSelectedPallets((prev) => {
      const cargoKey = `cargo-${cargoId}`;
      const currentPallets = prev[cargoKey] || [];

      if (currentPallets.includes(palletNumber)) {
        return {
          ...prev,
          [cargoKey]: currentPallets.filter((p) => p !== palletNumber),
        };
      }
      return {
        ...prev,
        [cargoKey]: [...currentPallets, palletNumber],
      };
    });
  };

  const handleMixedPalletToggle = (itemNumber, pallet) => {
    const isOnThisDelivery = pallet.delivery_id === deliveryNote.delivery_id;
    if (pallet.status && pallet.status !== 'available' && !isOnThisDelivery) {
      return;
    }
    setSelectedItemNumbers((prev) =>
      prev.includes(itemNumber)
        ? prev.filter((n) => n !== itemNumber)
        : [...prev, itemNumber]
    );
  };

  const handleSelectAllMixedPallets = () => {
    const available = mixedPallets
      .filter(
        (p) =>
          p.status === 'available' || p.delivery_id === deliveryNote.delivery_id
      )
      .map((p) => String(p.pallet_number));
    const allSelected =
      available.length > 0 && available.every((n) => selectedItemNumbers.includes(n));
    setSelectedItemNumbers(allSelected ? [] : available);
  };

  const handleSelectAllPallets = (item) => {
    const cargoKey = `cargo-${item.cargo_id}`;
    const availablePallets = (item.inventory?.pallets || [])
      .filter((p) => isPalletSelectable(p, deliveryNote.delivery_id))
      .map((p) => p.pallet_number);
    const currentPallets = selectedPallets[cargoKey] || [];

    if (currentPallets.length === availablePallets.length && availablePallets.length > 0) {
      setSelectedPallets((prev) => ({
        ...prev,
        [cargoKey]: [],
      }));
    } else {
      setSelectedPallets((prev) => ({
        ...prev,
        [cargoKey]: availablePallets,
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePalletWeightChange = (palletNumber, value) => {
    setPalletWeights((prev) => ({
      ...prev,
      [String(palletNumber)]: value,
    }));
  };

  const handlePalletWeightBlur = async (palletNumber) => {
    if (!canEditWeights || savingWeights) {
      return;
    }

    try {
      setSavingWeights(true);
      setError('');
      const weight = palletWeights[String(palletNumber)];
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          item_weights: [{
            pallet_number: String(palletNumber),
            pallet_weight_measured_kg: weight === '' || weight === null || weight === undefined
              ? null
              : weight,
          }],
        }),
      });

      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to save pallet weight'));
      }

      const data = await response.json();
      if (data.delivery_note?.items) {
        setDeliveryNote((prev) => ({ ...prev, items: data.delivery_note.items }));
        setPalletWeights(buildPalletWeightsFromItems(data.delivery_note.items));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingWeights(false);
    }
  };

  const getAvailablePalletsForAllocation = () => {
    if (isMixed) {
      return (mixedPallets || [])
        .filter((p) => p.status === 'available')
        .map((p) => ({
          key: String(p.pallet_number),
          pallet_number: String(p.pallet_number),
          label: `Item No. ${p.pallet_number}`,
          detail: (p.contents || [])
            .map((c) => `${c.material_description}: ${c.quantity_pieces}`)
            .join('; '),
        }));
    }

    const rows = [];
    (cargoItems || []).forEach((cargo) => {
      (cargo.inventory?.pallets || []).forEach((pallet) => {
        if (pallet.status === 'available') {
          rows.push({
            key: `${cargo.cargo_id}:${pallet.pallet_number}`,
            cargo_id: cargo.cargo_id,
            pallet_number: String(pallet.pallet_number),
            label: String(pallet.pallet_number),
            detail: `${cargo.material_description || 'Material'} · ${pallet.quantity ?? 0} pcs`,
          });
        }
      });
    });
    return rows;
  };

  const toggleManualAllocSelection = (key) => {
    setManualAllocSelections((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleManualAllocate = async () => {
    if (!canManualAllocate) {
      return;
    }
    if (manualAllocSelections.length === 0) {
      setError('Select at least one available pallet to allocate');
      return;
    }
    if (!manualAllocReason.trim()) {
      setError('Enter a comment/reason for this manual allocation');
      return;
    }

    const available = getAvailablePalletsForAllocation();
    const selectedRows = available.filter((row) => manualAllocSelections.includes(row.key));
    if (selectedRows.length === 0) {
      setError('Selected pallets are no longer available');
      return;
    }

    const payload = isMixed
      ? {
          pallet_numbers: selectedRows.map((row) => row.pallet_number),
          reason: manualAllocReason.trim(),
        }
      : {
          items: selectedRows.map((row) => ({
            cargo_id: row.cargo_id,
            pallet_number: row.pallet_number,
          })),
          reason: manualAllocReason.trim(),
        };

    try {
      setManualAllocating(true);
      setError('');
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}/allocate-pallets`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to allocate pallet(s)'));
      }
      const data = await response.json();
      setSuccess('Pallet(s) allocated to this delivery note');
      setManualAllocSelections([]);
      setManualAllocReason('');
      if (data.allocation_log) {
        setAllocationLogs(data.allocation_log);
      }
      setTimeout(() => {
        fetchDeliveryNote();
        setSuccess('');
      }, 800);
    } catch (err) {
      setError(err.message);
    } finally {
      setManualAllocating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this delivery note?')) return;

    try {
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete delivery note');
      }

      setSuccess('Delivery note deleted');
      setTimeout(() => navigate('/delivery-notes'), 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const markReadyRequest = async () => {
    const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}/status`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status: 'ready' })
    });

    if (!response.ok) throw new Error('Failed to update status');

    setSuccess('Marked as ready');
    setTimeout(() => fetchDeliveryNote(), 1000);
  };

  const handleMarkReady = async () => {
    if (!ensureWeightTotalMatches('markReady')) {
      return;
    }

    try {
      await markReadyRequest();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownloadPDF = () => {
    if (!ensureWeightTotalMatches('downloadPdf')) {
      return;
    }
    handleDocument('original', 'download');
  };

  const handleViewPDF = () => {
    if (!ensureWeightTotalMatches('viewPdf')) {
      return;
    }
    handleDocument('original', 'view');
  };

  const runPendingWeightAction = async () => {
    const action = pendingWeightAction;
    setPendingWeightAction(null);
    setShowWeightMismatchModal(false);
    try {
      if (action === 'markReady') {
        await markReadyRequest();
      } else if (action === 'downloadPdf') {
        await handleDocument('original', 'download');
      } else if (action === 'viewPdf') {
        await handleDocument('original', 'view');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFixPalletWeights = () => {
    setPendingWeightAction(null);
    setShowWeightMismatchModal(false);
  };

  const handleUpdateTotalLoadWeightFromModal = async () => {
    const value = weightMismatchLoadDraft;
    if (value === '' || !Number.isFinite(parseFloat(value))) {
      setError('Enter a valid Total Load Weight');
      return;
    }

    try {
      setSavingLoadWeight(true);
      setError('');
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ total_load_weight_kg: value }),
      });

      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to update Total Load Weight'));
      }

      const data = await response.json();
      if (data.delivery_note) {
        setDeliveryNote((prev) => ({ ...prev, ...data.delivery_note }));
        setEditForm((prev) => ({
          ...prev,
          total_load_weight_kg: data.delivery_note.total_load_weight_kg ?? '',
        }));
      } else {
        setDeliveryNote((prev) => ({ ...prev, total_load_weight_kg: parseFloat(value) }));
        setEditForm((prev) => ({ ...prev, total_load_weight_kg: value }));
      }

      setSuccess('Total Load Weight updated');
      setTimeout(() => setSuccess(''), 2000);
      await runPendingWeightAction();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingLoadWeight(false);
    }
  };

  const handleDownloadSigned = () => handleDocument('signed', 'download');
  const handleViewSigned = () => handleDocument('signed', 'view');

  const handleDeparture = async () => {
    console.log('handleDeparture called');
    console.log('departureDateTime:', departureDateTime);
    
    if (!departureDateTime) {
      setModalError('Please enter departure date and time');
      return;
    }

    try {
      setModalError('');
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'in_transit',
          departure_datetime: new Date(departureDateTime).toISOString()
        })
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to record departure'));
      }

      console.log('Departure recorded successfully');
      setSuccess('Departure recorded');
      setShowDepartureModal(false);
      setDepartureDateTime('');
      setModalError('');
      setTimeout(() => fetchDeliveryNote(), 1000);
    } catch (err) {
      setModalError(err.message);
    }
  };

  const handleArrival = async () => {
    if (!arrivalDateTime) {
      setModalError('Please enter arrival date and time');
      return;
    }

    if (deliveryNote?.departure_datetime) {
      const arrival = new Date(arrivalDateTime);
      const departure = parseApiDateTime(deliveryNote.departure_datetime);
      if (arrival < departure) {
        setModalError('Arrival date and time cannot be before departure date and time');
        return;
      }
    }

    try {
      setModalError('');
      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}/arrival`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          arrival_datetime: new Date(arrivalDateTime).toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(await readApiError(response, 'Failed to record arrival'));
      }

      setSuccess('Arrival recorded');
      setShowArrivalModal(false);
      setArrivalDateTime('');
      setModalError('');
      setTimeout(() => fetchDeliveryNote(), 1000);
    } catch (err) {
      setModalError(err.message);
    }
  };

  const handleUploadSigned = async () => {
    if (!uploadFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await fetch(`${API_URL}/delivery-notes/${deliveryId}/upload-signed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) throw new Error('Failed to upload signed document');

      setSuccess('Signed document uploaded. Status updated to Delivered');
      setShowUploadForm(false);
      setUploadFile(null);
      setUploading(false);
      setTimeout(() => fetchDeliveryNote(), 1500);
    } catch (err) {
      setError(err.message);
      setUploading(false);
    }
  };

  if (loading) return <div className="loading">Loading delivery note...</div>;
  if (loadError) return <div className="alert alert-danger">{loadError}</div>;
  if (!deliveryNote) return <div className="alert alert-danger">Delivery note not found</div>;

  const getStatusColor = (status) => {
    const colors = { draft: 'warning', ready: 'info', in_transit: 'primary', delivered: 'success' };
    return colors[status] || 'secondary';
  };

  const getStatusLabel = (status) => {
    const labels = { draft: 'Draft', ready: 'Ready', in_transit: 'In Transit', delivered: 'Delivered' };
    return labels[status] || status;
  };

  return (
    <div className="delivery-note-details-container">
      <div className="details-header">
        <h1>Delivery Note {deliveryNote.delivery_number}</h1>
        <span className={`status-badge badge-${getStatusColor(deliveryNote.status)}`}>
          {getStatusLabel(deliveryNote.status)}
        </span>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Shipment Details Block */}
      <ShipmentDetails 
        shipment={shipment} 
        packing_list={packing_list}
        loading={!shipment}
        canEdit={isAdmin}
        token={token}
        onUpdated={() => fetchDeliveryNote()}
      />

      {/* Timeline */}
      <div className="delivery-timeline">
        <div className={`timeline-step ${deliveryNote.status !== 'draft' || deliveryNote.status === 'draft' ? 'completed' : ''}`}>
          <div className="step-marker">✓</div>
          <div className="step-label">Created</div>
        </div>
        <div className={`timeline-step ${['ready', 'in_transit', 'delivered'].includes(deliveryNote.status) ? 'completed' : ''}`}>
          <div className="step-marker">✓</div>
          <div className="step-label">Ready</div>
        </div>
        <div className={`timeline-step ${['in_transit', 'delivered'].includes(deliveryNote.status) ? 'completed' : ''}`}>
          <div className="step-marker">🚚</div>
          <div className="step-label">In Transit</div>
        </div>
        <div className={`timeline-step ${deliveryNote.status === 'delivered' ? 'completed' : ''}`}>
          <div className="step-marker">✓</div>
          <div className="step-label">Delivered</div>
        </div>
      </div>

      {/* Delivery Details */}
      <div className="section">
        <h3>Delivery Information</h3>
        {!isEditing ? (
          <div className="info-display">
            <div className="info-row">
              <span className="label">Customer Name:</span>
              <span className="value">{deliveryNote.customer_name}</span>
            </div>
            <div className="info-row">
              <span className="label">Delivery Address:</span>
              <span className="value">{deliveryNote.delivery_address}</span>
            </div>
            <div className="info-row">
              <span className="label">City:</span>
              <span className="value">{deliveryNote.delivery_city}</span>
            </div>
            <div className="info-row">
              <span className="label">Country:</span>
              <span className="value">{deliveryNote.delivery_country}</span>
            </div>
            <div className="info-row">
              <span className="label">Total Load Weight:</span>
              <span className="value">
                {deliveryNote.total_load_weight_kg != null
                  ? `${Number(deliveryNote.total_load_weight_kg).toFixed(2)} kg`
                  : 'N/A'}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Total Number of Pallets:</span>
              <span className="value">
                {deliveryNote.total_pallet_count != null
                  ? deliveryNote.total_pallet_count
                  : 'N/A'}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Truck Model:</span>
              <span className="value">{deliveryNote.truck_model}</span>
            </div>
            <div className="info-row">
              <span className="label">Truck Registration:</span>
              <span className="value">{deliveryNote.truck_registration}</span>
            </div>
            <div className="info-row">
              <span className="label">Driver Name:</span>
              <span className="value">{deliveryNote.driver_name}</span>
            </div>
            <div className="info-row">
              <span className="label">Driver Phone:</span>
              <span className="value">{deliveryNote.driver_phone}</span>
            </div>
            <div className="info-row">
              <span className="label">Special Instructions:</span>
              <span className="value">{deliveryNote.notes || 'None'}</span>
            </div>
            {deliveryNote.departure_datetime && (
              <div className="info-row">
                <span className="label">Departure:</span>
                <span className="value">{parseApiDateTime(deliveryNote.departure_datetime)?.toLocaleString()}</span>
              </div>
            )}
            {deliveryNote.arrival_datetime && (
              <div className="info-row">
                <span className="label">Arrival:</span>
                <span className="value">{parseApiDateTime(deliveryNote.arrival_datetime)?.toLocaleString()}</span>
              </div>
            )}
          </div>
        ) : (
          <form className="edit-form">
            <input type="date" name="delivery_date" value={editForm.delivery_date} onChange={handleInputChange} placeholder="Delivery Date" />
            <input type="text" name="customer_name" value={editForm.customer_name} onChange={handleInputChange} placeholder="Customer Name" />
            <textarea name="delivery_address" value={editForm.delivery_address} onChange={handleInputChange} placeholder="Delivery Address" rows="3" />
            <input type="text" name="delivery_city" value={editForm.delivery_city} onChange={handleInputChange} placeholder="City" />
            <input type="text" name="delivery_country" value={editForm.delivery_country} onChange={handleInputChange} placeholder="Country" />
            <div className="edit-form-row">
              <input
                type="number"
                className="no-spinners"
                name="total_load_weight_kg"
                value={editForm.total_load_weight_kg}
                onChange={handleInputChange}
                placeholder="Total Load Weight (kg)"
                min="0"
                step="0.01"
              />
              <input
                type="number"
                className="no-spinners"
                name="total_pallet_count"
                value={editForm.total_pallet_count}
                onChange={handleInputChange}
                placeholder="Total Number of Pallets"
                min="0"
                step="1"
              />
            </div>
            <input type="text" name="truck_model" value={editForm.truck_model} onChange={handleInputChange} placeholder="Truck Model" />
            <input type="text" name="truck_registration" value={editForm.truck_registration} onChange={handleInputChange} placeholder="Truck Registration" />
            <input type="text" name="driver_name" value={editForm.driver_name} onChange={handleInputChange} placeholder="Driver Name" />
            <input type="text" name="driver_phone" value={editForm.driver_phone} onChange={handleInputChange} placeholder="Driver Phone" />
            <textarea name="notes" value={editForm.notes} onChange={handleInputChange} placeholder="Special instructions" rows="3" />
          </form>
        )}
      </div>

      {/* Pallets on delivery note */}
      <div className="section">
        <h3>{isMixed ? 'Item Numbers on Delivery Note' : 'Pallets on Delivery Note'}</h3>
        {!isEditing || !canEditPallets ? (
          <div className="delivery-pallets-display">
            {getUniqueDeliveryPallets().length > 0 ? (
              <table className="delivery-pallets-table">
                <thead>
                  <tr>
                    <th>{isMixed ? 'Item No.' : 'Pallet'}</th>
                    <th>{isMixed ? 'Materials' : 'Material'}</th>
                    {!isMixed && <th>Qty</th>}
                    <th>Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {getUniqueDeliveryPallets().map((row) => (
                    <tr key={row.pallet_number}>
                      <td>{row.pallet_number}</td>
                      {isMixed ? (
                        <td>
                          {(row.materials || []).map((line, idx) => (
                            <div key={`${row.pallet_number}-${idx}`}>
                              {line.material_description}: {line.quantity_delivered}
                            </div>
                          ))}
                        </td>
                      ) : (
                        <>
                          <td>{row.material_description}</td>
                          <td>{row.quantity_delivered}</td>
                        </>
                      )}
                      <td className="pallet-weight-cell">
                        {canEditWeights ? (
                          <input
                            type="number"
                            className="no-spinners pallet-weight-input"
                            min="0"
                            step="0.01"
                            value={palletWeights[String(row.pallet_number)] ?? ''}
                            onChange={(e) => handlePalletWeightChange(row.pallet_number, e.target.value)}
                            onBlur={() => handlePalletWeightBlur(row.pallet_number)}
                            placeholder="kg"
                            aria-label={`Weight for ${row.pallet_number}`}
                          />
                        ) : (
                          <span>
                            {row.pallet_weight_measured_kg !== '' && row.pallet_weight_measured_kg != null
                              ? `${Number(row.pallet_weight_measured_kg).toFixed(2)} kg`
                              : '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="weight-total-row">
                    <td colSpan={isMixed ? 2 : 3} className="weight-total-label">
                      Total weight
                    </td>
                    <td className="pallet-weight-cell weight-total-value">
                      {getPalletWeightTotal().toFixed(2)} kg
                    </td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <p className="no-pallets-message">No pallets on this delivery note.</p>
            )}
            {canEditWeights && savingWeights && (
              <p className="pallet-weight-saving">Saving weight...</p>
            )}
            {allPalletWeightsEntered() && getTotalLoadWeightValue() != null && !weightsMatchTotalLoad() && (
              <div className="weight-mismatch-banner" role="alert">
                <strong>Weight mismatch:</strong> pallet total ({getPalletWeightTotal().toFixed(2)} kg)
                {' '}does not match Total Load Weight ({getTotalLoadWeightValue().toFixed(2)} kg).
                {' '}
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => openWeightMismatchModal(null)}
                >
                  Resolve
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="pallet-selection delivery-note-pallet-edit">
            <div className="pallet-filter-bar">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={palletFilter}
                onChange={(e) => setPalletFilter(e.target.value.replace(/[^\d]/g, ''))}
                placeholder={
                  isMixed
                    ? 'Filter by item number (optional — enter 3+ digits)'
                    : 'Filter by pallet number (enter at least 3 digits)'
                }
                maxLength={20}
              />
              {getFilterDigits().length > 0 && (
                <button
                  type="button"
                  className="pallet-filter-clear"
                  onClick={() => setPalletFilter('')}
                >
                  Clear filter
                </button>
              )}
            </div>

            {isMixed ? (
              <div className="mixed-pallet-selection">
                {getFilterDigits().length >= 3 && (
                  <p className="pallet-filter-summary">
                    Showing {getMixedPalletsToShow().length} item number(s) matching &quot;{getFilterDigits()}&quot;
                  </p>
                )}
                {mixedPallets.some(
                  (p) => p.status === 'available' || p.delivery_id === deliveryNote.delivery_id
                ) && (
                  <div className="select-all">
                    <input
                      type="checkbox"
                      id="dn-select-all-mixed"
                      checked={
                        mixedPallets
                          .filter(
                            (p) =>
                              p.status === 'available' ||
                              p.delivery_id === deliveryNote.delivery_id
                          )
                          .every((p) =>
                            selectedItemNumbers.includes(String(p.pallet_number))
                          ) &&
                        mixedPallets.some(
                          (p) =>
                            p.status === 'available' ||
                            p.delivery_id === deliveryNote.delivery_id
                        )
                      }
                      onChange={handleSelectAllMixedPallets}
                    />
                    <label htmlFor="dn-select-all-mixed">Select all available</label>
                  </div>
                )}
                <div className="pallets-grid mixed-pallets-grid">
                  {getMixedPalletsToShow().map((pallet, idx) => {
                    const itemNumber = String(pallet.pallet_number);
                    const isOnThisDelivery = pallet.delivery_id === deliveryNote.delivery_id;
                    const isAvailable = pallet.status === 'available' || isOnThisDelivery;
                    const isSelected = selectedItemNumbers.includes(itemNumber);
                    const contentsSummary = (pallet.contents || [])
                      .map((line) => `${line.material_description}: ${line.quantity_pieces}`)
                      .join('; ');

                    return (
                      <div
                        key={`dn-mixed-${pallet.pallet_id}-${idx}`}
                        className={`pallet-checkbox ${!isAvailable ? 'pallet-unavailable' : ''}`}
                      >
                        <input
                          type="checkbox"
                          id={`dn-mixed-pallet-${idx}`}
                          checked={isSelected}
                          disabled={!isAvailable}
                          onChange={() => handleMixedPalletToggle(itemNumber, pallet)}
                        />
                        <label htmlFor={`dn-mixed-pallet-${idx}`}>
                          <span className="pallet-label">Item No. {itemNumber}</span>
                          <span className="mixed-pallet-contents">{contentsSummary || 'No materials'}</span>
                          <span className={`pallet-status-tag status-${pallet.status || 'available'}`}>
                            {isOnThisDelivery ? 'on this note' : pallet.status}
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>
                <p className="selection-summary">
                  {selectedItemNumbers.length} item number(s) selected
                </p>
              </div>
            ) : isPalletFilterActive() ? (
              <div className="pallet-filter-results">
                <p className="pallet-filter-summary">
                  Showing {getFilteredPallets().length} pallet(s) matching &quot;{getFilterDigits()}&quot;
                </p>
                <div className="pallets-filter-grid">
                  {getFilteredPallets().map(({ item, palletNumber }, idx) => {
                    const palletInfo = getPalletInfo(item, palletNumber);
                    const selectable = isPalletSelectable(palletInfo, deliveryNote.delivery_id);
                    const isSelected = (selectedPallets[`cargo-${item.cargo_id}`] || []).includes(palletNumber);

                    return (
                      <div key={`dn-filter-${idx}`} className="filtered-pallet-item">
                        <span className="filtered-pallet-material">{item.material_description}</span>
                        <div className={`pallet-checkbox ${!selectable ? 'pallet-unavailable' : ''}`}>
                          <input
                            type="checkbox"
                            id={`dn-filter-pallet-${idx}`}
                            checked={isSelected}
                            disabled={!selectable}
                            onChange={() => handlePalletToggle(item.cargo_id, palletNumber, item)}
                          />
                          <label htmlFor={`dn-filter-pallet-${idx}`}>
                            <span className="pallet-label">{palletNumber}</span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="cargo-list">
                {cargoItems.map((item) => {
                  const cargoKey = `cargo-${item.cargo_id}`;
                  const selectablePallets = (item.inventory?.pallets || []).filter((p) =>
                    isPalletSelectable(p, deliveryNote.delivery_id)
                  );

                  return (
                    <div key={item.cargo_id} className="cargo-section">
                      <div className="cargo-header">
                        <h4>{item.material_description}</h4>
                        <span className="pallet-count">
                          {(selectedPallets[cargoKey] || []).length} / {selectablePallets.length} selected
                        </span>
                      </div>
                      {selectablePallets.length > 0 && (
                        <div className="select-all">
                          <input
                            type="checkbox"
                            id={`dn-select-all-${item.cargo_id}`}
                            checked={
                              selectablePallets.length > 0 &&
                              (selectedPallets[cargoKey] || []).length === selectablePallets.length
                            }
                            onChange={() => handleSelectAllPallets(item)}
                          />
                          <label htmlFor={`dn-select-all-${item.cargo_id}`}>Select all available</label>
                        </div>
                      )}
                      <div className="pallets-grid">
                        {(item.pallet_numbers || []).map((palletNumber, pIdx) => {
                          const palletInfo = getPalletInfo(item, palletNumber);
                          const selectable = isPalletSelectable(palletInfo, deliveryNote.delivery_id);
                          const isSelected = (selectedPallets[cargoKey] || []).includes(palletNumber);

                          return (
                            <div
                              key={`dn-${item.cargo_id}-${pIdx}`}
                              className={`pallet-checkbox ${!selectable ? 'pallet-unavailable' : ''}`}
                            >
                              <input
                                type="checkbox"
                                id={`dn-pallet-${item.cargo_id}-${pIdx}`}
                                checked={isSelected}
                                disabled={!selectable}
                                onChange={() => handlePalletToggle(item.cargo_id, palletNumber, item)}
                              />
                              <label htmlFor={`dn-pallet-${item.cargo_id}-${pIdx}`}>
                                <span className="pallet-label">{palletNumber}</span>
                                {palletInfo?.delivery_id === deliveryNote.delivery_id && (
                                  <span className="pallet-status-tag status-available">on this note</span>
                                )}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {canManualAllocate && (
        <div className="section manual-allocation-section">
          <h3>Admin: Manual Pallet Allocation</h3>
          <p className="manual-allocation-help">
            Use this only when a pallet was physically delivered but omitted from the delivery note.
            The pallet status will match this note ({deliveryNote.status}), and stock counts will update.
          </p>
          {getAvailablePalletsForAllocation().length === 0 ? (
            <p className="no-pallets-message">No available pallets left on this packing list.</p>
          ) : (
            <>
              <div className="manual-allocation-list">
                {getAvailablePalletsForAllocation().map((row) => (
                  <label key={row.key} className="manual-allocation-item">
                    <input
                      type="checkbox"
                      checked={manualAllocSelections.includes(row.key)}
                      onChange={() => toggleManualAllocSelection(row.key)}
                    />
                    <span className="manual-allocation-label">
                      <strong>{row.label}</strong>
                      {row.detail ? <span className="manual-allocation-detail">{row.detail}</span> : null}
                    </span>
                  </label>
                ))}
              </div>
              <div className="manual-allocation-reason">
                <label htmlFor="manual-alloc-reason">Comment / reason *</label>
                <textarea
                  id="manual-alloc-reason"
                  rows="3"
                  value={manualAllocReason}
                  onChange={(e) => setManualAllocReason(e.target.value)}
                  placeholder="Explain why this pallet is being allocated after the fact..."
                />
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleManualAllocate}
                disabled={manualAllocating}
              >
                {manualAllocating ? 'Allocating...' : 'Allocate selected pallet(s)'}
              </button>
            </>
          )}
          {allocationLogs.length > 0 && (
            <div className="allocation-log-list">
              <h4>Allocation history</h4>
              {allocationLogs
                .filter((log) => log.action === 'manual_pallet_allocation')
                .map((log) => (
                  <div key={log.log_id} className="allocation-log-entry">
                    <div className="allocation-log-meta">
                      {parseApiDateTime(log.action_timestamp)?.toLocaleString() || log.action_timestamp}
                      {log.performed_by_name ? ` · ${log.performed_by_name}` : ''}
                    </div>
                    <pre>{log.details}</pre>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="action-buttons">
        {deliveryNote.status === 'draft' && (
          <>
            <button className="btn btn-secondary" onClick={handleEdit}>Edit</button>
            <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
            <button className="btn btn-primary" onClick={handleMarkReady}>Mark Ready</button>
            {isEditing && (
              <>
                <button className="btn btn-success" onClick={handleSave}>Save</button>
                <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
              </>
            )}
          </>
        )}

        {deliveryNote.status === 'ready' && (
          <>
            {!isEditing ? (
              <>
                <button className="btn btn-secondary" onClick={handleEdit}>✏️ Edit</button>
                <button className="btn btn-danger" onClick={handleDelete}>🗑️ Delete</button>
                <button className="btn btn-secondary" onClick={handleViewPDF} disabled={documentLoading}>
                  👁 View PDF
                </button>
                <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={documentLoading}>
                  📥 Download PDF
                </button>
                <button 
                  className="btn btn-warning" 
                  onClick={() => {
                    setModalError('');
                    setShowDepartureModal(true);
                  }}
                >
                  🚚 Truck Departing
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-success" onClick={handleSave}>💾 Save</button>
                <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
              </>
            )}
          </>
        )}

        {deliveryNote.status === 'in_transit' && (
          <>
            <button className="btn btn-secondary" onClick={handleViewPDF} disabled={documentLoading}>
              👁 View PDF
            </button>
            <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={documentLoading}>
              📥 Download PDF
            </button>
            <button className="btn btn-success" onClick={() => { setModalError(''); setShowArrivalModal(true); }}>✓ Arrived at Customer</button>
            {deliveryNote.arrival_datetime && (
              <button className="btn btn-info" onClick={() => setShowUploadForm(true)}>📤 Upload Signed Copy</button>
            )}
          </>
        )}

        {deliveryNote.status === 'delivered' && (
          <>
            {deliveryNote.signed_document_path ? (
              <>
                <button className="btn btn-secondary" onClick={handleViewSigned} disabled={documentLoading}>
                  👁 View Signed Copy
                </button>
                <button className="btn btn-success" onClick={handleDownloadSigned} disabled={documentLoading}>
                  📄 Download Signed Copy
                </button>
                <button className="btn btn-secondary" onClick={handleViewPDF} disabled={documentLoading}>
                  👁 View Original PDF
                </button>
                <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={documentLoading}>
                  📥 Download Original PDF
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-secondary" onClick={handleViewPDF} disabled={documentLoading}>
                  👁 View PDF
                </button>
                <button className="btn btn-primary" onClick={handleDownloadPDF} disabled={documentLoading}>
                  📥 Download PDF
                </button>
              </>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showWeightMismatchModal && (
        <div className="modal-overlay weight-mismatch-modal-overlay">
          <div className="modal-content weight-mismatch-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Weight mismatch</h3>
            <p>
              Please re-check the pallet weights. The sum of pallet weights
              ({getPalletWeightTotal().toFixed(2)} kg) does not match the Total Load Weight
              ({getTotalLoadWeightValue() != null ? `${getTotalLoadWeightValue().toFixed(2)} kg` : 'not set'}).
            </p>
            <p>You can fix the individual pallet weights, or correct the Total Load Weight below.</p>
            <div className="weight-mismatch-load-edit">
              <label htmlFor="weight-mismatch-load-input">Total Load Weight (kg)</label>
              <input
                id="weight-mismatch-load-input"
                type="number"
                className="no-spinners"
                min="0"
                step="0.01"
                value={weightMismatchLoadDraft}
                onChange={(e) => setWeightMismatchLoadDraft(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setWeightMismatchLoadDraft(getPalletWeightTotal().toFixed(2))}
              >
                Use pallet total
              </button>
            </div>
            <div className="modal-buttons">
              <button type="button" className="btn btn-secondary" onClick={handleFixPalletWeights}>
                Fix pallet weights
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpdateTotalLoadWeightFromModal}
                disabled={savingLoadWeight}
              >
                {savingLoadWeight ? 'Saving...' : 'Update Total Load Weight'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDepartureModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowDepartureModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{margin: '0 0 20px 0', color: '#1a3a52'}}>Record Truck Departure</h3>
            {modalError && (
              <div className="alert alert-danger" style={{ marginBottom: '15px' }}>{modalError}</div>
            )}
            <p style={{color: '#666', fontSize: '13px', marginBottom: '15px'}}>
              Enter the date and time when the truck departs
            </p>
            <input
              type="datetime-local"
              value={departureDateTime}
              onChange={(e) => {
                setDepartureDateTime(e.target.value);
                setModalError('');
              }}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '20px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button 
                onClick={() => setShowDepartureModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeparture}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2c5aa0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Record Departure
              </button>
            </div>
          </div>
        </div>
      )}

      {showArrivalModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowArrivalModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{margin: '0 0 20px 0', color: '#1a3a52'}}>Record Truck Arrival</h3>
            {modalError && (
              <div className="alert alert-danger" style={{ marginBottom: '15px' }}>{modalError}</div>
            )}
            <p style={{color: '#666', fontSize: '13px', marginBottom: '15px'}}>
              Enter the date and time when the truck arrives at the customer
            </p>
            <input
              type="datetime-local"
              value={arrivalDateTime}
              min={toDatetimeLocalValue(deliveryNote?.departure_datetime)}
              onChange={(e) => {
                setArrivalDateTime(e.target.value);
                setModalError('');
              }}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '20px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button 
                onClick={() => setShowArrivalModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleArrival}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2c5aa0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Record Arrival
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadForm && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => { setShowUploadForm(false); setUploadFile(null); }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{margin: '0 0 20px 0', color: '#1a3a52'}}>Upload Signed Delivery Note</h3>
            <p style={{color: '#666', fontSize: '13px', marginBottom: '15px'}}>
              Upload the signed delivery note (PDF or JPG)
            </p>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg"
              onChange={(e) => setUploadFile(e.target.files[0])}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
            {uploadFile && (
              <p style={{fontSize: '12px', color: '#666', marginBottom: '15px'}}>
                ✓ Selected: {uploadFile.name}
              </p>
            )}
            <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
              <button 
                onClick={() => { setShowUploadForm(false); setUploadFile(null); }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleUploadSigned}
                disabled={uploading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: uploading ? '#ccc' : '#2c5aa0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {documentViewer && (
        <div className="document-viewer-overlay" onClick={closeDocumentViewer}>
          <div className="document-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="document-viewer-header">
              <h3>{documentViewer.title}</h3>
              <button type="button" className="document-viewer-close" onClick={closeDocumentViewer}>
                Close
              </button>
            </div>
            <div className="document-viewer-body">
              {documentViewer.type === 'pdf' ? (
                <iframe
                  src={documentViewer.url}
                  title={documentViewer.title}
                  className="document-viewer-frame"
                />
              ) : (
                <img
                  src={documentViewer.url}
                  alt={documentViewer.title}
                  className="document-viewer-image"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
